/**
 * MoixiShuffle — aleatoriza (Fisher-Yates) el orden de las tarjetas de producto
 * en las secciones de colección marcadas con data-shuffle-enabled="true".
 *
 * No reemplaza el flujo AJAX nativo de filtros/orden de theme.js; se monta
 * encima de él vigilando [data-partial] con un MutationObserver.
 *
 * Carga como script clásico (sin defer/module) en snippets/theme-globals.liquid,
 * ANTES del parseo del <body>. Por eso este archivo solo define el namespace a
 * nivel superior; toda interacción con el DOM ocurre dentro de DOMContentLoaded.
 */
(function () {
  'use strict';

  var sectionStates = new Map(); // container -> { order, perPage, total, active, currentPage, observer }
  var observedContainers = new WeakSet();

  // ---------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------

  function fisherYates(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  function fnv1a(str) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }
    return hash.toString(16);
  }

  function isRealReload() {
    try {
      var entries = performance.getEntriesByType('navigation');
      return entries.length > 0 && entries[0].type === 'reload';
    } catch (e) {
      return false;
    }
  }

  function getPageFromLocation() {
    var p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return p && p > 0 ? p : 1;
  }

  // 'default' = orden por defecto (shuffle elegible), null = orden manual (shuffle desactivado)
  function getSortToken(container) {
    var params = new URLSearchParams(window.location.search);
    var sortBy = params.get('sort_by');
    var defaultSort = container.dataset.defaultSortBy || '';
    if (!sortBy || sortBy === defaultSort) return 'default';
    return null;
  }

  function isEligible(container) {
    if (container.dataset.shuffleEnabled !== 'true') return false;
    return getSortToken(container) === 'default';
  }

  function buildCacheKey(container) {
    var handle = container.dataset.collectionHandle || 'na';
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('sort_by');
    var keys = [];
    params.forEach(function (_, k) {
      if (keys.indexOf(k) === -1) keys.push(k);
    });
    keys.sort();
    var canonical = keys
      .map(function (k) {
        return k + '=' + params.getAll(k).sort().join(',');
      })
      .join('&');
    var filtersHash = fnv1a(canonical);
    return 'moixi_shuffle:v1:' + handle + ':' + filtersHash + ':default';
  }

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.warn('[MoixiShuffle] No se pudo guardar en sessionStorage', e);
    }
  }

  function clearCache(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* noop */
    }
  }

  // ---------------------------------------------------------------------
  // Fetch del catálogo/resultado filtrado completo
  // ---------------------------------------------------------------------

  function extractCards(partialEl) {
    var grid = partialEl.querySelector('.collection__products');
    if (!grid) return [];
    return Array.prototype.slice
      .call(grid.querySelectorAll(':scope > .product-item'))
      .map(function (el) {
        return el.outerHTML;
      });
  }

  function fetchSectionPage(container, page) {
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', container.dataset.sectionId);
    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  function fetchCollectionOrder(container) {
    return fetchSectionPage(container, 1).then(function (doc1) {
      var partial1 = doc1.querySelector('[data-partial]');
      if (!partial1) return { total: 0, perPage: 0, cards: [] };

      var total = parseInt(partial1.dataset.collectionProductsCount || '0', 10);
      var sectionRoot1 = doc1.querySelector('[data-section-type="collection"]');
      var cards1 = extractCards(partial1);
      var perPage =
        parseInt((sectionRoot1 && sectionRoot1.dataset.collectionItemsPerPage) || '0', 10) ||
        cards1.length;

      if (!perPage || total <= 0) {
        return { total: total, perPage: perPage, cards: cards1 };
      }

      var totalPages = Math.ceil(total / perPage);
      var cardsByPage = new Array(totalPages);
      cardsByPage[0] = cards1;

      var remaining = [];
      for (var p = 2; p <= totalPages; p++) remaining.push(p);

      var concurrency = 4;
      var idx = 0;

      function worker() {
        if (idx >= remaining.length) return Promise.resolve();
        var page = remaining[idx++];
        return fetchSectionPage(container, page)
          .then(function (doc) {
            var partial = doc.querySelector('[data-partial]');
            cardsByPage[page - 1] = partial ? extractCards(partial) : [];
          })
          .catch(function () {
            cardsByPage[page - 1] = [];
          })
          .then(worker);
      }

      var workers = [];
      for (var w = 0; w < Math.min(concurrency, remaining.length); w++) {
        workers.push(worker());
      }

      return Promise.all(workers).then(function () {
        var allCards = [].concat.apply([], cardsByPage);
        return { total: total, perPage: perPage, cards: allCards };
      });
    });
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function reveal(container) {
    container.setAttribute('data-shuffle-ready', 'true');
  }

  function reinitSection(container) {
    try {
      var S = window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
      if (!S || !S.instances || !S.registered) return;
      var type = container.getAttribute('data-section-type');
      var TypedSection = S.registered[type];
      if (!TypedSection) return;
      for (var i = S.instances.length - 1; i >= 0; i--) {
        var instance = S.instances[i];
        if (instance && instance.container === container) {
          if (typeof instance.onUnload === 'function') instance.onUnload();
          S.instances.splice(i, 1);
        }
      }
      S.instances.push(new TypedSection(container));
    } catch (e) {
      // Reinicialización best-effort: si la API interna del tema cambia, el
      // orden sigue siendo correcto pero swatches/hover/quick-add podrían
      // no re-vincularse. Verificar en QA visual tras cada despliegue.
      console.warn('[MoixiShuffle] No se pudo reinicializar la interactividad de la sección', e);
    }
  }

  function updatePaginationActiveState(container, page) {
    var partial = container.querySelector('[data-partial]');
    if (!partial) return;
    var items = partial.querySelectorAll('.pagination__item');
    items.forEach(function (li) {
      var link = li.querySelector('[data-page]');
      if (!link) return;
      var p = parseInt(link.getAttribute('data-page'), 10);
      if (!p) return;
      if (p === page) {
        li.setAttribute('data-type', 'active');
      } else if (li.getAttribute('data-type') === 'active') {
        li.setAttribute('data-type', 'none');
      }
    });
  }

  function renderSlice(container, order, page, perPage) {
    var partial = container.querySelector('[data-partial]');
    if (!partial) {
      reveal(container);
      return;
    }
    var grid = partial.querySelector('.collection__products');
    if (!grid) {
      reveal(container);
      return;
    }
    var start = (page - 1) * perPage;
    var slice = order.slice(start, start + perPage);
    grid.innerHTML = slice.join('');
    updatePaginationActiveState(container, page);
    reinitSection(container);
    reveal(container);
  }

  // ---------------------------------------------------------------------
  // Paginación virtual (pushState / popstate)
  // ---------------------------------------------------------------------

  function goToVirtualPage(container, state, page, pushHistory) {
    renderSlice(container, state.order, page, state.perPage);
    state.currentPage = page;
    if (pushHistory) {
      var url = new URL(window.location.href);
      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page);
      }
      history.pushState({ moixiShufflePage: page }, '', url.toString());
    }
    container.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[data-page]');
    if (!link) return;
    var container = link.closest('[data-shuffle-enabled="true"]');
    if (!container) return;
    var state = sectionStates.get(container);
    if (!state || !state.active) return; // sin shuffle activo: dejar que navegue de verdad
    var page = parseInt(link.getAttribute('data-page'), 10);
    if (!page || page < 1) return;
    e.preventDefault();
    goToVirtualPage(container, state, page, true);
  });

  window.addEventListener('popstate', function () {
    sectionStates.forEach(function (state, container) {
      if (!state.active) return;
      goToVirtualPage(container, state, getPageFromLocation(), false);
    });
  });

  // ---------------------------------------------------------------------
  // Guardia frente al flujo AJAX nativo de filtros/orden (theme.js)
  // ---------------------------------------------------------------------
  //
  // Nuestro propio renderSlice() solo reescribe .collection__products (hijo
  // de [data-partial]), nunca [data-partial] en sí, así que un observer con
  // childList:true (sin subtree) sobre [data-partial] SOLO se dispara cuando
  // el flujo nativo reemplaza this.partial.innerHTML completo (cambio de
  // filtro/orden, o su propio popstate) — no hace falta distinguir "propia
  // escritura" de "externa".

  function attachGuardOnce(container) {
    if (observedContainers.has(container)) return;
    var partial = container.querySelector('[data-partial]');
    if (!partial) return;
    observedContainers.add(container);
    var observer = new MutationObserver(function () {
      handleExternalChange(container);
    });
    observer.observe(partial, { childList: true });
  }

  function handleExternalChange(container) {
    container.removeAttribute('data-shuffle-ready'); // re-oculta antes del próximo pintado
    bootSection(container);
  }

  // ---------------------------------------------------------------------
  // Arranque por sección
  // ---------------------------------------------------------------------

  function bootSection(container) {
    var partial = container.querySelector('[data-partial]');
    if (!partial) return;
    attachGuardOnce(container);

    if (!isEligible(container)) {
      sectionStates.set(container, { active: false });
      reveal(container);
      return;
    }

    var key = buildCacheKey(container);
    if (isRealReload()) clearCache(key);

    var cached = readCache(key);
    if (cached && cached.order && cached.order.length) {
      var cachedState = {
        order: cached.order,
        perPage: cached.perPage,
        total: cached.total,
        active: true,
        currentPage: getPageFromLocation(),
      };
      sectionStates.set(container, cachedState);
      renderSlice(container, cachedState.order, cachedState.currentPage, cachedState.perPage);
      return;
    }

    fetchCollectionOrder(container)
      .then(function (result) {
        if (!result.perPage || !result.cards.length) {
          sectionStates.set(container, { active: false });
          reveal(container);
          return;
        }
        var shuffled = fisherYates(result.cards.slice());
        var state = {
          order: shuffled,
          perPage: result.perPage,
          total: result.total,
          active: true,
          currentPage: getPageFromLocation(),
        };
        sectionStates.set(container, state);
        writeCache(key, {
          order: shuffled,
          perPage: result.perPage,
          total: result.total,
          createdAt: Date.now(),
        });
        renderSlice(container, state.order, state.currentPage, state.perPage);
      })
      .catch(function (e) {
        console.warn('[MoixiShuffle] Fallo al traer el catálogo completo, se muestra el orden por defecto', e);
        sectionStates.set(container, { active: false });
        reveal(container);
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-shuffle-enabled="true"]').forEach(bootSection);
  });

  window.MoixiShuffle = {
    fisherYates: fisherYates,
    isEligible: isEligible,
    getCacheKey: buildCacheKey,
  };
})();
